// utils/rssGenerator.js
import { apiEndpoints, fetchAPI, API_BASE_URL } from "./api";

export class RSSGenerator {
  constructor(siteData = {}) {
    this.siteData = {
      title: siteData.title || "Red Arauca",
      description: siteData.description || "Noticias de Arauca y Colombia",
      link: siteData.link || API_BASE_URL,
      language: siteData.language || "es-co",
      copyright: siteData.copyright || "2025 Red Arauca",
      webMaster: siteData.webMaster || "webmaster@redarauca.com",
      managingEditor: siteData.managingEditor || "editor@redarauca.com",
      image: siteData.image || `${API_BASE_URL}/assets/logo.png`,
    };
  }

  // Escape XML characters
  escapeXML(text) {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Format date for RSS
  formatRSSDate(date) {
    return new Date(date).toUTCString();
  }

  // Generate RSS item from post
  generateRSSItem(post) {
    const link = `${this.siteData.link}/post/${post.slug}`;
    const description = post.excerpt || post.description || "";
    const imageUrl = post.featured_image
      ? `${API_BASE_URL}/uploads/${post.featured_image}`
      : this.siteData.image;

    return `
    <item>
      <title><![CDATA[${this.escapeXML(post.title)}]]></title>
      <link>${link}</link>
      <guid isPermaLink="true">${link}</guid>
      <description><![CDATA[${this.escapeXML(description)}]]></description>
      <pubDate>${this.formatRSSDate(
        post.published_at || post.created_at
      )}</pubDate>
      <author>${this.escapeXML(
        post.author || this.siteData.managingEditor
      )}</author>
      <category><![CDATA[${this.escapeXML(
        post.category || "Noticias"
      )}]]></category>
      <enclosure url="${imageUrl}" type="image/jpeg" length="0"/>
      <source url="${this.siteData.link}/rss.xml">${
      this.siteData.title
    }</source>
    </item>`;
  }

  // Generate complete RSS feed
  async generateRSSFeed(limit = 50) {
    try {
      const response = await fetchAPI(apiEndpoints.posts.list(1, limit));
      const posts = response?.data || [];

      const rssItems = posts.map((post) => this.generateRSSItem(post)).join("");
      const lastBuildDate =
        posts.length > 0
          ? this.formatRSSDate(posts[0].updated_at || posts[0].created_at)
          : this.formatRSSDate(new Date());

      return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" 
     xmlns:content="http://purl.org/rss/1.0/modules/content/"
     xmlns:dc="http://purl.org/dc/elements/1.1/"
     xmlns:atom="http://www.w3.org/2005/Atom"
     xmlns:sy="http://purl.org/rss/1.0/modules/syndication/"
     xmlns:slash="http://purl.org/rss/1.0/modules/slash/">
  <channel>
    <title><![CDATA[${this.siteData.title}]]></title>
    <description><![CDATA[${this.siteData.description}]]></description>
    <link>${this.siteData.link}</link>
    <atom:link href="${this.siteData.link}/rss.xml" rel="self" type="application/rss+xml"/>
    <language>${this.siteData.language}</language>
    <copyright><![CDATA[${this.siteData.copyright}]]></copyright>
    <webMaster>${this.siteData.webMaster}</webMaster>
    <managingEditor>${this.siteData.managingEditor}</managingEditor>
    <lastBuildDate>${lastBuildDate}</lastBuildDate>
    <generator>Red Arauca RSS Generator</generator>
    <ttl>60</ttl>
    <image>
      <url>${this.siteData.image}</url>
      <title><![CDATA[${this.siteData.title}]]></title>
      <link>${this.siteData.link}</link>
      <width>144</width>
      <height>144</height>
    </image>
    ${rssItems}
  </channel>
</rss>`;
    } catch (error) {
      console.error("Error generando RSS feed:", error);
      return null;
    }
  }

  // Generate category-specific RSS
  async generateCategoryRSS(categorySlug, limit = 20) {
    try {
      const response = await fetchAPI(
        apiEndpoints.posts.list(1, limit, categorySlug)
      );
      const posts = response?.data || [];

      const categoryTitle = `${this.siteData.title} - ${categorySlug}`;
      const categoryDescription = `Noticias de ${categorySlug} en ${this.siteData.title}`;

      const rssItems = posts.map((post) => this.generateRSSItem(post)).join("");

      return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0">
  <channel>
    <title><![CDATA[${categoryTitle}]]></title>
    <description><![CDATA[${categoryDescription}]]></description>
    <link>${this.siteData.link}/categoria/${categorySlug}</link>
    <language>${this.siteData.language}</language>
    <lastBuildDate>${this.formatRSSDate(new Date())}</lastBuildDate>
    ${rssItems}
  </channel>
</rss>`;
    } catch (error) {
      console.error("Error generando RSS de categoría:", error);
      return null;
    }
  }
}

// Hook para usar el generador RSS
export const useRSSGenerator = () => {
  const generator = new RSSGenerator();

  const downloadRSSFeed = async () => {
    const rssXML = await generator.generateRSSFeed();
    if (rssXML) {
      const blob = new Blob([rssXML], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rss.xml";
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const downloadCategoryRSS = async (categorySlug) => {
    const rssXML = await generator.generateCategoryRSS(categorySlug);
    if (rssXML) {
      const blob = new Blob([rssXML], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rss-${categorySlug}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return {
    downloadRSSFeed,
    downloadCategoryRSS,
    generator,
  };
};
